import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useBaiduMap } from './BaiduMap'

export interface BaiduInfoWindowProps {
    position: { lat: number; lng: number }
    onCloseClick?: () => void
    children: React.ReactNode
}

/**
 * Baidu Map 위에 커스텀 InfoWindow를 렌더링
 * 네이티브 BMapGL.InfoWindow 대신 React Portal로 메인 React 트리에서 렌더링
 */
const BaiduInfoWindow: React.FC<BaiduInfoWindowProps> = ({ position, onCloseClick, children }) => {
    const map = useBaiduMap()
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [pixel, setPixel] = useState<{ x: number; y: number } | null>(null)

    // 맵 컨테이너를 찾아서 Portal 대상으로 사용
    useEffect(() => {
        if (!map) return

        const mapContainer = map.getContainer() as HTMLElement
        if (!mapContainer) return

        // 오버레이 컨테이너 생성 (맵 컨테이너 내부에 추가)
        let overlay = mapContainer.querySelector('.baidu-info-overlay-container') as HTMLDivElement
        if (!overlay) {
            overlay = document.createElement('div')
            overlay.className = 'baidu-info-overlay-container'
            overlay.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:999;'
            mapContainer.style.position = 'relative'
            mapContainer.appendChild(overlay)
        }
        containerRef.current = overlay

        // 좌표 -> 픽셀 변환 함수
        const updatePixel = () => {
            const point = new window.BMapGL.Point(position.lng, position.lat)
            const px = map.pointToOverlayPixel(point)
            if (px) {
                setPixel({ x: px.x, y: px.y })
            }
        }

        updatePixel()

        // 맵 이동/줌 시 위치 업데이트
        map.addEventListener('moving', updatePixel)
        map.addEventListener('moveend', updatePixel)
        map.addEventListener('zoomend', updatePixel)
        map.addEventListener('resize', updatePixel)

        return () => {
            map.removeEventListener('moving', updatePixel)
            map.removeEventListener('moveend', updatePixel)
            map.removeEventListener('zoomend', updatePixel)
            map.removeEventListener('resize', updatePixel)
        }
    }, [map, position.lat, position.lng])

    if (!containerRef.current || !pixel) return null

    return createPortal(
        <div
            style={{
                position: 'absolute',
                left: pixel.x,
                top: pixel.y,
                transform: 'translate(-50%, -100%)',
                marginTop: -12,
                pointerEvents: 'auto',
                zIndex: 1000,
            }}
        >
            {/* 말풍선 */}
            <div
                style={{
                    background: 'white',
                    borderRadius: 8,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    minWidth: 200,
                    maxWidth: 300,
                    position: 'relative',
                }}
            >
                {/* 닫기 버튼 */}
                <button
                    onClick={onCloseClick}
                    style={{
                        position: 'absolute',
                        top: 6,
                        right: 8,
                        background: 'none',
                        border: 'none',
                        fontSize: 18,
                        cursor: 'pointer',
                        color: '#666',
                        lineHeight: 1,
                        padding: '2px 4px',
                        zIndex: 1,
                    }}
                >
                    ×
                </button>
                {/* 컨텐츠 */}
                <div style={{ padding: '4px 0' }}>
                    {children}
                </div>
            </div>
            {/* 말풍선 꼬리 */}
            <div
                style={{
                    width: 0,
                    height: 0,
                    borderLeft: '10px solid transparent',
                    borderRight: '10px solid transparent',
                    borderTop: '10px solid white',
                    margin: '0 auto',
                    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))',
                }}
            />
        </div>,
        containerRef.current
    )
}

export default BaiduInfoWindow
